import {
  Card,
  DatePicker,
  Drawer,
  Empty,
  Grid,
  Layout,
  Progress,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { ApexOptions } from "apexcharts";
import { motion } from "framer-motion";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import {
  Activity,
  BarChart3,
  CheckCircle2,
  Clock,
  Medal,
  RefreshCw,
  Target,
  UserX,
  Users,
  Zap,
} from "lucide-react";
import { useGetQuizStatistics, useGetLeaderboard, useGetQuizDetails, useGetQuizParticipants, useGetPlayerStatistics, useRefetchCountdown } from "@hooks";
import type { User } from "@types";
import type {
  CategoryAccuracyStatDto,
  HardestQuestionStatDto,
  LeaderboardEntryDto,
  PlayerMatchHistoryDto,
  PlayerQuestionStatDto,
  QuizParticipantStatusDto,
  QuizParticipationStatDto,
  QuizStatisticsFilter,
} from "../../types/quiz-statistics";

const { RangePicker } = DatePicker;

interface QuizStatisticsPageProps {
  user: User;
}

export const QuizStatisticsPage = ({ user }: QuizStatisticsPageProps) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const isAdmin = user.applicationRoles.some((r) =>
    r.name !== undefined && ["Admin", "ExamAdmin", "DataAnalyst"].includes(r.name)
  );

  const defaultFilter = useMemo<QuizStatisticsFilter>(
    () => ({
      startDate: dayjs().subtract(30, "day").startOf("day").toISOString(),
      endDate: dayjs().endOf("day").toISOString(),
    }),
    []
  );

  const [filter, setFilter] = useState<QuizStatisticsFilter>(defaultFilter);
  const [selectedExamId, setSelectedExamId] = useState<string | null>(null);
  const [selectedPlayerUserId, setSelectedPlayerUserId] = useState<string | null>(null);

  const { data: stats, isLoading, dataUpdatedAt } = useGetQuizStatistics(filter);
  const { data: leaderboard, isLoading: isLeaderboardLoading } = useGetLeaderboard();
  const { data: quizDetails, isLoading: isQuizDetailsLoading, dataUpdatedAt: quizDetailsUpdatedAt } = useGetQuizDetails(
    selectedExamId,
    filter,
    !!selectedExamId
  );
  const { data: quizParticipants, isLoading: isParticipantsLoading, dataUpdatedAt: quizParticipantsUpdatedAt } = useGetQuizParticipants(
    selectedExamId,
    filter,
    !!selectedExamId
  );

  const { data: playerStats, isLoading: isPlayerStatsLoading } = useGetPlayerStatistics(
    selectedExamId,
    selectedPlayerUserId,
    filter,
    !!selectedExamId && !!selectedPlayerUserId
  );

  const REFETCH_INTERVAL = 15_000;
  const countdownSeconds = useRefetchCountdown(dataUpdatedAt, REFETCH_INTERVAL);
  const countdownProgress = Math.round((countdownSeconds / (REFETCH_INTERVAL / 1000)) * 100);

  // Drawer-level countdowns (use the earlier of quizDetails / quizParticipants updates)
  const quizDrawerUpdatedAt = Math.max(quizDetailsUpdatedAt, quizParticipantsUpdatedAt);
  const quizDrawerCountdown = useRefetchCountdown(quizDrawerUpdatedAt, REFETCH_INTERVAL);
  const quizDrawerProgress = Math.round((quizDrawerCountdown / (REFETCH_INTERVAL / 1000)) * 100);

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilter({
        startDate: dates[0].startOf("day").toISOString(),
        endDate: dates[1].endOf("day").toISOString(),
      });
    } else {
      setFilter({ startDate: null, endDate: null });
    }
  };

  const stripHtml = (html: string) =>
    html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const renderHardestQuestionAnswers = (answers: HardestQuestionStatDto["answers"]) => {
    if (!answers || answers.length === 0) {
      return <Typography.Text type="secondary">—</Typography.Text>;
    }

    return (
      <div style={{ padding: "8px 16px" }}>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {answers.map((answer, index) => (
            <div
              key={`${answer.answerText}-${index}`}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                background: answer.isCorrect ? "#f6ffed" : "#fafafa",
                border: `1px solid ${answer.isCorrect ? "#b7eb8f" : "#f0f0f0"}`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <Typography.Text strong={answer.isCorrect} style={{ fontSize: 13 }}>
                    {stripHtml(answer.answerText || "—")}
                  </Typography.Text>
                  {answer.isCorrect && (
                    <Tag color="green" style={{ marginInlineEnd: 0, flexShrink: 0 }}>
                      {t("quizStatistics.hardestQuestions.correctAnswer")}
                    </Tag>
                  )}
                </div>
                <Typography.Text type="secondary" style={{ flexShrink: 0, marginLeft: 12 }}>
                  {answer.selectionCount}× · {answer.selectionRate.toFixed(1)}%
                </Typography.Text>
              </div>
              <Progress
                percent={parseFloat(answer.selectionRate.toFixed(1))}
                size="small"
                showInfo={false}
                strokeColor={answer.isCorrect ? "#52c41a" : "#1890ff"}
                trailColor="#e8e8e8"
              />
            </div>
          ))}
        </Space>
      </div>
    );
  };

  const getParticipantStatus = (participant: QuizParticipantStatusDto) => {
    if (participant.awaitingOpponentCount > 0) {
      return {
        key: "waitingForOpponent",
        color: "gold",
        label: t("quizStatistics.quizDetails.waitingForOpponent"),
      };
    }

    if (participant.hasPlayed) {
      return {
        key: "played",
        color: "green",
        label: t("quizStatistics.quizDetails.played"),
      };
    }

    return {
      key: "notPlayed",
      color: "default",
      label: t("quizStatistics.quizDetails.notPlayed"),
    };
  };

  // ---------- Trend chart ----------
  const trendOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "area",
        height: isMobile ? 260 : 220,
        toolbar: { show: false },
        animations: { enabled: true, speed: 400 },
      },
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05 },
      },
      colors: ["#13c2c2"],
      xaxis: {
        categories: stats?.dailyMatchCounts.map((d) => d.date) ?? [],
        labels: { rotate: -45, style: { fontSize: "11px" } },
      },
      yaxis: {
        labels: { formatter: (v: number) => String(Math.round(v)) },
        min: 0,
      },
      grid: { borderColor: "#f0f0f0" },
      dataLabels: { enabled: false },
    }),
    [isMobile, stats]
  );

  const trendSeries = useMemo(
    () => [
      {
        name: t("quizStatistics.chart.matchCount"),
        data: stats?.dailyMatchCounts.map((d) => d.matchCount) ?? [],
      },
    ],
    [stats, t]
  );

  // ---------- Category bar chart ----------
  const categoryOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "bar", height: isMobile ? 320 : 280, toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "60%",
          dataLabels: { position: "top" },
        },
      },
      colors: ["#52c41a"],
      dataLabels: {
        enabled: true,
        formatter: (v: number) => `${v}%`,
        offsetX: 22,
        style: { fontSize: "12px", colors: ["#333"] },
      },
      xaxis: {
        categories: stats?.categoryAccuracy.map((c) => c.categoryName) ?? [],
        max: 100,
        labels: { formatter: (v: string) => `${v}%` },
      },
      tooltip: { y: { formatter: (v: number) => `${v}%` } },
      grid: { borderColor: "#f0f0f0" },
    }),
    [isMobile, stats]
  );

  const categorySeries = useMemo(
    () => [
      {
        name: t("quizStatistics.chart.accuracy"),
        data: stats?.categoryAccuracy.map((c) => c.accuracyRate) ?? [],
      },
    ],
    [stats, t]
  );

  // ---------- Hourly activity heatmap ----------
  const DAYS_HR = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];
  const DAYS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayLabels = t("quizStatistics.sections.peakHours").startsWith("Peak") ? DAYS_EN : DAYS_HR;

  const heatmapSeries = useMemo(() => {
    return dayLabels.map((dayName, dayIdx) => ({
      name: dayName,
      data: Array.from({ length: 24 }, (_, hour) => {
        const entry = stats?.hourlyActivity.find(
          (h) => h.dayOfWeek === dayIdx && h.hour === hour
        );
        return { x: `${String(hour).padStart(2, "0")}:00`, y: entry?.matchCount ?? 0 };
      }),
    }));
  }, [stats, dayLabels]);

  const heatmapOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "heatmap", toolbar: { show: false }, animations: { enabled: false } },
      dataLabels: { enabled: false },
      colors: ["#13c2c2"],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.65,
          radius: 2,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: "#f5f5f5", name: "0" },
              { from: 1, to: 3, color: "#b5e8e8", name: "1–3" },
              { from: 4, to: 8, color: "#5cc5c5", name: "4–8" },
              { from: 9, to: 999, color: "#006d6d", name: "9+" },
            ],
          },
        },
      },
      xaxis: { labels: { style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v: number) => `${v} mečeva` } },
      grid: { padding: { right: 20 } },
    }),
    []
  );

  // ---------- Quiz participation columns ----------
  const quizParticipationColumns: ColumnsType<QuizParticipationStatDto> = [
    {
      title: "#",
      key: "rank",
      width: 40,
      render: (_v: unknown, _r: unknown, idx: number) => (
        <Typography.Text type="secondary">{idx + 1}</Typography.Text>
      ),
    },
    {
      title: t("quizStatistics.quizParticipation.examName"),
      dataIndex: "examName",
      key: "examName",
      ellipsis: true,
      render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: t("quizStatistics.quizParticipation.matchType"),
      dataIndex: "matchType",
      key: "matchType",
      width: 170,
      filters: [
        { text: t("quizStatistics.quizParticipation.matchTypeTournament"), value: "Tournament" },
        { text: t("quizStatistics.quizParticipation.matchTypeOneTime"), value: "OneTimeMatching" },
        { text: t("quizStatistics.quizParticipation.matchTypeDirect"), value: "Direct" },
      ],
      onFilter: (value, record: QuizParticipationStatDto) => record.matchType === value,
      render: (v: string) => {
        const map: Record<string, { color: string; label: string }> = {
          Tournament: { color: "purple", label: t("quizStatistics.quizParticipation.matchTypeTournament") },
          OneTimeMatching: { color: "blue", label: t("quizStatistics.quizParticipation.matchTypeOneTime") },
          Direct: { color: "default", label: t("quizStatistics.quizParticipation.matchTypeDirect") },
        };
        const entry = map[v] ?? { color: "default", label: v };
        return <Tag color={entry.color}>{entry.label}</Tag>;
      },
    },
    {
      title: t("quizStatistics.quizParticipation.uniquePlayers"),
      dataIndex: "uniquePlayerCount",
      key: "uniquePlayerCount",
      align: "center",
      sorter: (a: QuizParticipationStatDto, b: QuizParticipationStatDto) => b.uniquePlayerCount - a.uniquePlayerCount,
      render: (v: number) => (
        <Typography.Text strong style={{ color: "#1890ff" }}>{v}</Typography.Text>
      ),
    },
    {
      title: t("quizStatistics.quizParticipation.matchCount"),
      dataIndex: "matchCount",
      key: "matchCount",
      align: "center",
      sorter: (a: QuizParticipationStatDto, b: QuizParticipationStatDto) => b.matchCount - a.matchCount,
      render: (v: number) => <Typography.Text>{v}</Typography.Text>,
    },
    {
      title: t("quizStatistics.quizParticipation.avgScore"),
      dataIndex: "avgScore",
      key: "avgScore",
      align: "center",
      render: (v: number) => <Typography.Text>{Math.round(v)}</Typography.Text>,
    },
  ];

  // ---------- Hardest questions columns ----------
  const questionColumns: ColumnsType<HardestQuestionStatDto> = [
    {
      title: t("quizStatistics.questions.text"),
      dataIndex: "questionText",
      key: "questionText",
      ellipsis: true,
      render: (v: string) => <Typography.Text>{stripHtml(v)}</Typography.Text>,
    },
    {
      title: t("quizStatistics.questions.accuracy"),
      dataIndex: "accuracyRate",
      key: "accuracyRate",
      width: 160,
      render: (v: number) => (
        <Progress
          percent={v}
          size="small"
          strokeColor={v >= 60 ? "#52c41a" : v >= 40 ? "#faad14" : "#ff4d4f"}
          format={(p) => `${p}%`}
          style={{ minWidth: 120 }}
        />
      ),
    },
    {
      title: t("quizStatistics.questions.avgTime"),
      dataIndex: "avgResponseTimeMs",
      key: "avgResponseTimeMs",
      width: 120,
      align: "center",
      render: (v: number) => `${(v / 1000).toFixed(1)}s`,
    },
    {
      title: t("quizStatistics.questions.total"),
      dataIndex: "totalAnswers",
      key: "totalAnswers",
      width: 80,
      align: "center",
    },
  ];

  // ---------- Category table columns ----------
  const categoryColumns: ColumnsType<CategoryAccuracyStatDto> = [
    {
      title: t("quizStatistics.categories.name"),
      dataIndex: "categoryName",
      key: "categoryName",
      render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: t("quizStatistics.categories.accuracy"),
      dataIndex: "accuracyRate",
      key: "accuracyRate",
      render: (v: number) => (
        <Progress
          percent={v}
          strokeColor={v >= 60 ? "#52c41a" : v >= 40 ? "#faad14" : "#ff4d4f"}
          format={(p) => `${p}%`}
          style={{ minWidth: 140 }}
        />
      ),
    },
    {
      title: t("quizStatistics.categories.total"),
      dataIndex: "totalAnswers",
      key: "totalAnswers",
      width: 100,
      align: "center",
    },
  ];

  // ---------- ELO leaderboard columns ----------
  const leaderboardColumns: ColumnsType<LeaderboardEntryDto> = [
    {
      title: "#",
      dataIndex: "rank",
      key: "rank",
      width: 48,
      render: (v: number) => {
        if (v === 1) return <span style={{ fontSize: 18 }}>🥇</span>;
        if (v === 2) return <span style={{ fontSize: 18 }}>🥈</span>;
        if (v === 3) return <span style={{ fontSize: 18 }}>🥉</span>;
        return <Typography.Text type="secondary">{v}</Typography.Text>;
      },
    },
    {
      title: t("quizStatistics.leaderboard.player"),
      dataIndex: "userDisplayName",
      key: "userDisplayName",
      render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: t("quizStatistics.leaderboard.rating"),
      dataIndex: "rating",
      key: "rating",
      align: "center",
      render: (v: number) => (
        <Typography.Text strong style={{ color: "#722ED1", fontSize: 15 }}>
          {v}
        </Typography.Text>
      ),
    },
    {
      title: t("quizStatistics.players.wins"),
      dataIndex: "wins",
      key: "wins",
      align: "center",
      render: (v: number) => (
        <Typography.Text style={{ color: "#52c41a" }}>{v}</Typography.Text>
      ),
    },
    {
      title: t("quizStatistics.players.losses"),
      dataIndex: "losses",
      key: "losses",
      align: "center",
      render: (v: number) => (
        <Typography.Text style={{ color: "#ff4d4f" }}>{v}</Typography.Text>
      ),
    },
    {
      title: t("quizStatistics.players.winRate"),
      dataIndex: "winRate",
      key: "winRate",
      align: "center",
      render: (v: number) => (
        <Progress
          percent={v}
          size="small"
          strokeColor={v >= 60 ? "#52c41a" : v >= 40 ? "#faad14" : "#ff4d4f"}
          format={(p) => `${p}%`}
          style={{ minWidth: 90 }}
        />
      ),
    },
    {
      title: t("quizStatistics.leaderboard.totalMatches"),
      dataIndex: "totalMatches",
      key: "totalMatches",
      align: "center",
      render: (v: number) => <Typography.Text>{v}</Typography.Text>,
    },
  ];

  // ---------- KPI cards ----------
  const kpiCards = useMemo(() => {
    if (!stats) return [];
    return [
      {
        key: "totalMatches",
        icon: <Activity size={20} color="#13c2c2" />,
        title: t("quizStatistics.kpi.totalMatches"),
        value: stats.totalMatches,
      },
      {
        key: "completedMatches",
        icon: <CheckCircle2 size={20} color="#52c41a" />,
        title: t("quizStatistics.kpi.completedMatches"),
        value: stats.completedMatches,
      },
      {
        key: "abandonRate",
        icon: <UserX size={20} color="#fa8c16" />,
        title: t("quizStatistics.kpi.abandonRate"),
        value: stats.totalMatches > 0
          ? Math.round((stats.totalMatches - stats.completedMatches) / stats.totalMatches * 100)
          : 0,
        suffix: "%",
      },
      {
        key: "globalAccuracy",
        icon: <Target size={20} color="#722ED1" />,
        title: t("quizStatistics.kpi.globalAccuracy"),
        value: stats.globalAccuracyPct,
        suffix: "%",
      },
      {
        key: "avgTime",
        icon: <Clock size={20} color="#1890ff" />,
        title: t("quizStatistics.kpi.avgResponseTime"),
        value: (stats.avgResponseTimeMs / 1000).toFixed(1),
        suffix: "s",
      },
      {
        key: "topPlayer",
        icon: <Zap size={20} color="#ff4d4f" />,
        title: t("quizStatistics.kpi.topPlayer"),
        value: leaderboard?.[0]?.userDisplayName ?? "—",
        isText: true,
      },
    ];
  }, [stats, t]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 1 }}>
      <Layout>
        {/* Header + filter */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "flex-start",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#13c2c2",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <div>
              <Typography.Title level={2} style={{ margin: 0 }}>
                {t("quizStatistics.title")}
              </Typography.Title>
              <Typography.Text type="secondary">
                {t("quizStatistics.subtitle")}
              </Typography.Text>
            </div>
          </div>

          <Space style={{ width: isMobile ? "100%" : undefined }} align="center">
            <RangePicker
              defaultValue={[
                dayjs().subtract(30, "day").startOf("day"),
                dayjs().endOf("day"),
              ]}
              onChange={handleDateChange}
              format="DD.MM.YYYY"
              style={{ width: isMobile ? "100%" : undefined }}
            />
            <div
              title={t("quizStatistics.refetchIn", { seconds: countdownSeconds })}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 20,
                background: "#f0fffe",
                border: "1px solid #b5f0f0",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              <RefreshCw
                size={13}
                color="#13c2c2"
                style={{
                  animation: countdownSeconds <= 2 ? "spin 0.6s linear infinite" : undefined,
                }}
              />
              <Progress
                type="circle"
                percent={countdownProgress}
                size={28}
                strokeColor="#13c2c2"
                trailColor="#e6f7ff"
                strokeWidth={10}
                format={() => (
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#13c2c2" }}>
                    {countdownSeconds}s
                  </span>
                )}
              />
              {!isMobile && (
                <Typography.Text style={{ fontSize: 12, color: "#08979c" }}>
                  {t("quizStatistics.refetchIn", { seconds: countdownSeconds })}
                </Typography.Text>
              )}
            </div>
          </Space>
        </div>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: 80 }}>
            <Spin size="large" />
          </div>
        ) : !stats ? (
          <Empty description={t("quizStatistics.noData")} />
        ) : (
          <>
            {/* KPI row */}
            <div
              style={{
                display: "flex",
                flexWrap: isMobile ? "wrap" : "nowrap",
                gap: 16,
                marginBottom: 20,
              }}
            >
              {kpiCards.map((card) => (
                <div key={card.key} style={{ flex: "1 1 0", minWidth: isMobile ? "100%" : 0 }}>
                  <Card size="small" style={{ height: "100%" }}>
                    <Space direction="vertical" size={4} style={{ width: "100%" }}>
                      <Space size={8}>
                        {card.icon}
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {card.title}
                        </Typography.Text>
                      </Space>
                      {card.isText ? (
                        <Typography.Text strong style={{ fontSize: 18 }}>
                          {card.value}
                        </Typography.Text>
                      ) : (
                        <Statistic
                          value={card.value}
                          suffix={card.suffix}
                          valueStyle={{ fontSize: 22, fontWeight: 700 }}
                        />
                      )}
                    </Space>
                  </Card>
                </div>
              ))}
            </div>

            {/* Trend chart */}
            <Card
              title={t("quizStatistics.sections.trend")}
              style={{ marginBottom: 20 }}
            >
              {stats.dailyMatchCounts.length === 0 ? (
                <Empty description={t("quizStatistics.noData")} />
              ) : (
                <Chart
                  options={trendOptions}
                  series={trendSeries}
                  type="area"
                  height={isMobile ? 260 : 220}
                />
              )}
            </Card>

            {/* ELO Leaderboard */}
            <Card
              title={
                <Space>
                  <Medal size={16} color="#722ED1" />
                  {t("quizStatistics.sections.eloLeaderboard")}
                </Space>
              }
              style={{ marginBottom: 20 }}
            >
              {isLeaderboardLoading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Spin />
                </div>
              ) : !leaderboard || leaderboard.length === 0 ? (
                <Empty description={t("quizStatistics.noData")} />
              ) : (
                <Table<LeaderboardEntryDto>
                  rowKey="userId"
                  dataSource={leaderboard}
                  columns={leaderboardColumns}
                  pagination={false}
                  size="small"
                  scroll={{ x: 720 }}
                  rowClassName={(_, idx) =>
                    idx < 3 ? "ant-table-row-top3" : ""
                  }
                />
              )}
            </Card>

            {/* Quiz participation per exam */}
            <Card
              title={t("quizStatistics.sections.quizParticipation")}
              style={{ marginBottom: 20 }}
            >
              {stats.quizParticipationStats.length === 0 ? (
                <Empty description={t("quizStatistics.noData")} />
              ) : (
                <Table<QuizParticipationStatDto>
                  rowKey="examId"
                  dataSource={stats.quizParticipationStats}
                  columns={quizParticipationColumns}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  size="small"
                  scroll={{ x: 560 }}
                  onRow={(record) => ({
                    onClick: () => setSelectedExamId(record.examId),
                    style: { cursor: "pointer" },
                  })}
                />
              )}
            </Card>

            {/* Category accuracy */}
            <Card
              title={t("quizStatistics.sections.categoryAccuracy")}
              style={{ marginBottom: 20 }}
            >
              {stats.categoryAccuracy.length === 0 ? (
                <Empty description={t("quizStatistics.noData")} />
              ) : stats.categoryAccuracy.length > 6 ? (
                <Chart
                  options={categoryOptions}
                  series={categorySeries}
                  type="bar"
                  height={Math.max(280, stats.categoryAccuracy.length * 36)}
                />
              ) : (
                <Table<CategoryAccuracyStatDto>
                  rowKey="categoryName"
                  dataSource={stats.categoryAccuracy}
                  columns={categoryColumns}
                  pagination={false}
                  size="small"
                  scroll={{ x: 520 }}
                />
              )}
            </Card>

            {/* Peak hours heatmap */}
            <Card
              title={t("quizStatistics.sections.peakHours")}
              style={{ marginBottom: 20 }}
            >
              {stats.hourlyActivity.length === 0 ? (
                <Empty description={t("quizStatistics.noData")} />
              ) : (
                <Chart
                  options={heatmapOptions}
                  series={heatmapSeries}
                  type="heatmap"
                  height={280}
                />
              )}
            </Card>

            {/* Hardest questions - admin only */}
            {isAdmin && (
            <Card title={t("quizStatistics.sections.hardestQuestions")}>
              {stats.hardestQuestions.length === 0 ? (
                <Empty description={t("quizStatistics.noData")} />
              ) : (
                <Table<HardestQuestionStatDto>
                  rowKey="questionCopyId"
                  dataSource={stats.hardestQuestions}
                  columns={questionColumns}
                  pagination={false}
                  size="middle"
                  scroll={{ x: 600 }}
                  expandable={{
                    expandedRowRender: (record) => renderHardestQuestionAnswers(record.answers),
                    rowExpandable: (record) => record.answers && record.answers.length > 0,
                  }}
                />
              )}
            </Card>
            )}
          </>
        )}
      </Layout>

      {/* Quiz details drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 32 }}>
            <span>{quizDetails?.examName ?? selectedExamId ?? ""}</span>
            {!!selectedExamId && (
              <div
                title={t("quizStatistics.refetchIn", { seconds: quizDrawerCountdown })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "3px 10px",
                  borderRadius: 20,
                  background: "#f0fffe",
                  border: "1px solid #b5f0f0",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  fontSize: 12,
                }}
              >
                <RefreshCw
                  size={13}
                  color="#13c2c2"
                  style={{ animation: quizDrawerCountdown <= 2 ? "spin 0.6s linear infinite" : undefined }}
                />
                <Progress
                  type="circle"
                  percent={quizDrawerProgress}
                  size={28}
                  strokeColor="#13c2c2"
                  trailColor="#e6f7ff"
                  strokeWidth={10}
                  format={() => (
                    <span style={{ fontSize: 9, fontWeight: 700, color: "#13c2c2" }}>
                      {quizDrawerCountdown}s
                    </span>
                  )}
                />
                <Typography.Text style={{ fontSize: 12, color: "#08979c" }}>
                  {t("quizStatistics.refetchIn", { seconds: quizDrawerCountdown })}
                </Typography.Text>
              </div>
            )}
          </div>
        }
        open={!!selectedExamId}
        onClose={() => setSelectedExamId(null)}
        rootClassName="quiz-statistics-drawer"
        width="100vw"
        styles={{ body: { paddingBottom: 24 } }}
      >
        <Tabs
          defaultActiveKey="stats"
          items={[
            {
              key: "stats",
              label: (
                <Space size={8}>
                  <BarChart3 size={16} />
                  <span>{t("quizStatistics.quizDetails.tabStats")}</span>
                </Space>
              ),
              children: isQuizDetailsLoading ? (
                <div style={{ textAlign: "center", paddingTop: 60 }}>
                  <Spin size="large" />
                </div>
              ) : quizDetails ? (
                <>
                  {/* KPI summary */}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                    {[
                      { label: t("quizStatistics.quizDetails.totalMatches"), value: quizDetails.totalMatches, color: "#1890ff" },
                      { label: t("quizStatistics.quizDetails.completedMatches"), value: quizDetails.completedMatches, color: "#52c41a" },
                      { label: t("quizStatistics.quizDetails.uniquePlayers"), value: quizDetails.uniquePlayerCount, color: "#722ed1" },
                      { label: t("quizStatistics.quizDetails.avgScore"), value: quizDetails.avgScore.toFixed(1), color: "#fa8c16" },
                      { label: t("quizStatistics.quizDetails.accuracy"), value: `${quizDetails.globalAccuracyPct.toFixed(1)}%`, color: "#eb2f96" },
                    ].map((kpi) => (
                      <Card
                        key={kpi.label}
                        size="small"
                        style={{ flex: "1 1 120px", minWidth: 100, textAlign: "center" }}
                      >
                        <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{kpi.label}</div>
                      </Card>
                    ))}
                  </div>

                  {/* Top players */}
                  {quizDetails.topPlayers.length > 0 && (
                    <Card
                      title={t("quizStatistics.quizDetails.topPlayers")}
                      size="small"
                      style={{ marginBottom: 16 }}
                    >
                      <Table
                        rowKey="userId"
                        dataSource={quizDetails.topPlayers}
                        pagination={false}
                        size="small"
                        onRow={(record) => ({
                          onClick: () => setSelectedPlayerUserId(record.userId),
                          style: { cursor: "pointer" },
                        })}
                        columns={[
                          { title: "#", key: "rank", width: 36, render: (_: unknown, __: unknown, i: number) => i + 1 },
                          {
                            title: t("quizStatistics.quizDetails.player"),
                            dataIndex: "displayName",
                            key: "displayName",
                            render: (v: string) => (
                              <Typography.Text strong style={{ color: "#1890ff", textDecoration: "underline" }}>
                                {v}
                              </Typography.Text>
                            ),
                          },
                          { title: t("quizStatistics.quizDetails.wins"), dataIndex: "wins", key: "wins", align: "center" },
                          { title: t("quizStatistics.quizDetails.winRate"), dataIndex: "winRate", key: "winRate", align: "center", render: (v: number) => <Tag color={(v ?? 0) >= 60 ? "green" : (v ?? 0) >= 40 ? "blue" : "default"}>{(v ?? 0).toFixed(1)}%</Tag> },
                          { title: t("quizStatistics.quizDetails.avgScore"), dataIndex: "avgScore", key: "avgScore", align: "center", render: (v: number) => <Typography.Text strong style={{ color: "#fa8c16" }}>{Math.round(v ?? 0)}</Typography.Text> },
                        ]}
                      />
                    </Card>
                  )}

                  {/* Category accuracy */}
                  {quizDetails.categoryAccuracy.length > 0 && (
                    <Card
                      title={t("quizStatistics.sections.categoryAccuracy")}
                      size="small"
                      style={{ marginBottom: 16 }}
                    >
                      <Table
                        rowKey="categoryName"
                        dataSource={quizDetails.categoryAccuracy}
                        pagination={false}
                        size="small"
                        columns={[
                          { title: t("quizStatistics.categoryAccuracy.category"), dataIndex: "categoryName", key: "categoryName", ellipsis: true },
                          { title: t("quizStatistics.categoryAccuracy.accuracy"), dataIndex: "accuracyRate", key: "accuracyRate", align: "center", render: (v: number) => <Tag color={(v ?? 0) >= 70 ? "green" : (v ?? 0) >= 40 ? "orange" : "red"}>{(v ?? 0).toFixed(1)}%</Tag> },
                          { title: t("quizStatistics.categoryAccuracy.totalAnswers"), dataIndex: "totalAnswers", key: "totalAnswers", align: "center" },
                        ]}
                      />
                    </Card>
                  )}

                  {/* Hardest questions — admin only */}
                  {isAdmin && quizDetails.hardestQuestions.length > 0 && (
                    <Card
                      title={t("quizStatistics.sections.hardestQuestions")}
                      size="small"
                      style={{ marginBottom: 16 }}
                    >
                      <Table
                        rowKey="questionCopyId"
                        dataSource={quizDetails.hardestQuestions}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: t("quizStatistics.hardestQuestions.question"),
                            dataIndex: "questionText",
                            key: "questionText",
                            ellipsis: true,
                            render: (v: string) => <Typography.Text>{stripHtml(v)}</Typography.Text>,
                          },
                          {
                            title: t("quizStatistics.hardestQuestions.incorrectRate"),
                            dataIndex: "accuracyRate",
                            key: "accuracyRate",
                            align: "center",
                            width: 120,
                            render: (v: number) => { const incorrect = 100 - (v ?? 0); return <Tag color={incorrect >= 70 ? "red" : incorrect >= 40 ? "orange" : "green"}>{incorrect.toFixed(1)}%</Tag>; },
                          },
                          {
                            title: t("quizStatistics.hardestQuestions.timesAnswered"),
                            dataIndex: "totalAnswers",
                            key: "totalAnswers",
                            align: "center",
                            width: 100,
                          },
                        ]}
                        expandable={{
                          expandedRowRender: (record: HardestQuestionStatDto) => renderHardestQuestionAnswers(record.answers),
                          rowExpandable: (record: HardestQuestionStatDto) => record.answers && record.answers.length > 0,
                        }}
                      />
                    </Card>
                  )}
                </>
              ) : null,
            },
            {
              key: "participants",
              label: (
                <Space size={8}>
                  <Users size={16} />
                  <span>{t("quizStatistics.quizDetails.tabParticipants")}</span>
                </Space>
              ),
              children: isParticipantsLoading ? (
                <div style={{ textAlign: "center", paddingTop: 60 }}>
                  <Spin size="large" />
                </div>
              ) : quizParticipants && quizParticipants.length > 0 ? (
                (() => {
                  const played = quizParticipants.filter(p => p.matchCount > 0).length;
                  const awaiting = quizParticipants.filter(p => p.matchCount === 0 && p.awaitingOpponentCount > 0).length;
                  const notPlayed = quizParticipants.filter(p => p.matchCount === 0 && p.awaitingOpponentCount === 0).length;
                  const total = quizParticipants.length;
                  return (
                    <>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                        <div style={{ flex: 1, minWidth: 120, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#52c41a" }}>{played}</div>
                          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{t("quizStatistics.quizDetails.played")}</div>
                          <div style={{ fontSize: 11, color: "#52c41a" }}>{total > 0 ? ((played / total) * 100).toFixed(0) : 0}%</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 120, background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#faad14" }}>{awaiting}</div>
                          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{t("quizStatistics.quizDetails.waitingForOpponent")}</div>
                          <div style={{ fontSize: 11, color: "#faad14" }}>{total > 0 ? ((awaiting / total) * 100).toFixed(0) : 0}%</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 120, background: "#fafafa", border: "1px solid #d9d9d9", borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#999" }}>{notPlayed}</div>
                          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{t("quizStatistics.quizDetails.notPlayed")}</div>
                          <div style={{ fontSize: 11, color: "#999" }}>{total > 0 ? ((notPlayed / total) * 100).toFixed(0) : 0}%</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 120, background: "#e6f7ff", border: "1px solid #91d5ff", borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#1890ff" }}>{total}</div>
                          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{t("quizStatistics.quizDetails.uniquePlayers")}</div>
                        </div>
                      </div>
                      <Table<QuizParticipantStatusDto>
                  rowKey="participantId"
                  dataSource={quizParticipants}
                  size="small"
                  pagination={{ pageSize: 15, hideOnSinglePage: true }}
                  scroll={{ x: 500 }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (record.matchCount > 0 || record.awaitingOpponentCount > 0) {
                        setSelectedPlayerUserId(record.participantId);
                      }
                    },
                    style: {
                      cursor: record.matchCount > 0 || record.awaitingOpponentCount > 0 ? "pointer" : "default",
                    },
                  })}
                  columns={[
                    {
                      title: t("quizStatistics.quizDetails.player"),
                      dataIndex: "displayName",
                      key: "displayName",
                      ellipsis: true,
                      render: (v: string, record: QuizParticipantStatusDto) =>
                        record.matchCount > 0 || record.awaitingOpponentCount > 0 ? (
                          <Typography.Text strong style={{ color: "#1890ff", textDecoration: "underline" }}>{v}</Typography.Text>
                        ) : (
                          <Typography.Text strong>{v}</Typography.Text>
                        ),
                    },
                    {
                      title: t("quizStatistics.quizDetails.email"),
                      dataIndex: "email",
                      key: "email",
                      ellipsis: true,
                      render: (v: string) => <Typography.Text type="secondary">{v}</Typography.Text>,
                    },
                    {
                      title: t("quizStatistics.quizDetails.status"),
                      dataIndex: "hasPlayed",
                      key: "hasPlayed",
                      align: "center",
                      width: 180,
                      filters: [
                        { text: t("quizStatistics.quizDetails.played"), value: "played" },
                        { text: t("quizStatistics.quizDetails.waitingForOpponent"), value: "waitingForOpponent" },
                        { text: t("quizStatistics.quizDetails.notPlayed"), value: "notPlayed" },
                      ],
                      onFilter: (value, record: QuizParticipantStatusDto) => getParticipantStatus(record).key === value,
                      render: (_: boolean, record: QuizParticipantStatusDto) => {
                        const status = getParticipantStatus(record);
                        return <Tag color={status.color}>{status.label}</Tag>;
                      },
                    },
                    {
                      title: t("quizStatistics.quizDetails.matchCount"),
                      dataIndex: "matchCount",
                      key: "matchCount",
                      align: "center",
                      width: 80,
                      sorter: (a: QuizParticipantStatusDto, b: QuizParticipantStatusDto) => b.matchCount - a.matchCount,
                    },
                    {
                      title: t("quizStatistics.quizDetails.awaitingOpponent"),
                      dataIndex: "awaitingOpponentCount",
                      key: "awaitingOpponentCount",
                      align: "center",
                      width: 120,
                      sorter: (a: QuizParticipantStatusDto, b: QuizParticipantStatusDto) => b.awaitingOpponentCount - a.awaitingOpponentCount,
                      render: (v: number) => (v > 0 ? <Tag color="gold">{v}</Tag> : "—"),
                    },
                    {
                      title: t("quizStatistics.quizDetails.winRate"),
                      dataIndex: "winRate",
                      key: "winRate",
                      align: "center",
                      width: 90,
                      sorter: (a: QuizParticipantStatusDto, b: QuizParticipantStatusDto) => b.winRate - a.winRate,
                      render: (v: number, record: QuizParticipantStatusDto) =>
                        record.matchCount > 0 ? <Tag color={(v ?? 0) >= 60 ? "green" : (v ?? 0) >= 40 ? "blue" : "default"}>{(v ?? 0).toFixed(1)}%</Tag> : "—",
                    },
                  ]}
                />
                    </>
                  );
                })()
              ) : (
                <Empty description={t("quizStatistics.noData")} />
              ),
            },
          ]}
        />
      </Drawer>

      {/* Player statistics nested drawer */}
      <Drawer
        title={
          playerStats ? (
            <Space>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#722ED1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {playerStats.displayName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{playerStats.displayName}</div>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>{playerStats.email}</div>
              </div>
            </Space>
          ) : (
            t("quizStatistics.player.title")
          )
        }
        open={!!selectedPlayerUserId}
        onClose={() => setSelectedPlayerUserId(null)}
        rootClassName="quiz-statistics-drawer"
        width="100vw"
        styles={{ body: { paddingBottom: 24 } }}
      >
        {isPlayerStatsLoading ? (
          <div style={{ textAlign: "center", paddingTop: 80 }}>
            <Spin size="large" />
          </div>
        ) : !playerStats ? (
          <Empty description={t("quizStatistics.noData")} />
        ) : (
          <Tabs
            defaultActiveKey="overview"
            items={[
              // ── Overview ──────────────────────────────────────────────────
              {
                key: "overview",
                label: (
                  <Space size={6} align="center">
                    <BarChart3 size={16} />
                    <span>{t("quizStatistics.player.tabOverview")}</span>
                  </Space>
                ),
                children: (
                  <>
                    {/* KPI */}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                      {[
                        { label: t("quizStatistics.player.totalMatches"), value: playerStats.totalMatches, color: "#1890ff" },
                        { label: t("quizStatistics.player.wins"), value: playerStats.wins, color: "#52c41a" },
                        { label: t("quizStatistics.player.losses"), value: playerStats.losses, color: "#ff4d4f" },
                        { label: t("quizStatistics.player.winRate"), value: `${playerStats.winRate.toFixed(1)}%`, color: "#722ED1" },
                        { label: t("quizStatistics.player.avgScore"), value: Math.round(playerStats.avgScore), color: "#fa8c16" },
                        { label: t("quizStatistics.player.accuracy"), value: `${playerStats.globalAccuracyPct.toFixed(1)}%`, color: "#eb2f96" },
                        { label: t("quizStatistics.player.avgResponseTime"), value: `${(playerStats.avgResponseTimeMs / 1000).toFixed(1)}s`, color: "#13c2c2" },
                      ].map((kpi) => (
                        <Card
                          key={kpi.label}
                          size="small"
                          style={{ flex: "1 1 110px", minWidth: 90, textAlign: "center" }}
                        >
                          <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{kpi.label}</div>
                        </Card>
                      ))}
                    </div>

                    {/* Category accuracy */}
                    {playerStats.categoryAccuracy.length > 0 && (
                      <Card
                        title={t("quizStatistics.player.categoryAccuracy")}
                        size="small"
                        style={{ marginBottom: 16 }}
                      >
                        <Table
                          rowKey="categoryName"
                          dataSource={playerStats.categoryAccuracy}
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: t("quizStatistics.categories.name"),
                              dataIndex: "categoryName",
                              key: "categoryName",
                              render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
                            },
                            {
                              title: t("quizStatistics.categories.accuracy"),
                              dataIndex: "accuracyRate",
                              key: "accuracyRate",
                              render: (v: number) => (
                                <Progress
                                  percent={v}
                                  size="small"
                                  strokeColor={v >= 70 ? "#52c41a" : v >= 40 ? "#faad14" : "#ff4d4f"}
                                  format={(p) => `${p}%`}
                                  style={{ minWidth: 140 }}
                                />
                              ),
                            },
                            {
                              title: t("quizStatistics.categories.total"),
                              dataIndex: "totalAnswers",
                              key: "totalAnswers",
                              width: 80,
                              align: "center",
                            },
                          ]}
                        />
                      </Card>
                    )}
                  </>
                ),
              },

              // ── Match History ─────────────────────────────────────────────
              {
                key: "matches",
                label: (
                  <Space size={6} align="center">
                    <Activity size={16} />
                    <span>{t("quizStatistics.player.tabMatches")}</span>
                  </Space>
                ),
                children: playerStats.matches.length === 0 ? (
                  <Empty description={t("quizStatistics.noData")} />
                ) : (
                  <Table
                    rowKey="matchId"
                    dataSource={playerStats.matches}
                    size="small"
                    pagination={{ pageSize: 15, hideOnSinglePage: true }}
                    scroll={{ x: 500 }}
                    columns={[
                      {
                        title: t("quizStatistics.player.matchDate"),
                        dataIndex: "playedAt",
                        key: "playedAt",
                        width: 140,
                        render: (v: string) => <Typography.Text type="secondary">{v}</Typography.Text>,
                      },
                      {
                        title: t("quizStatistics.player.opponent"),
                        dataIndex: "opponentName",
                        key: "opponentName",
                        render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
                      },
                      {
                        title: t("quizStatistics.player.result"),
                        dataIndex: "isWin",
                        key: "isWin",
                        width: 110,
                        align: "center",
                        filters: [
                          { text: t("quizStatistics.player.win"), value: true },
                          { text: t("quizStatistics.player.loss"), value: false },
                        ],
                        onFilter: (value, record: PlayerMatchHistoryDto) => record.isWin === value,
                        render: (v: boolean, record: PlayerMatchHistoryDto) =>
                          !record.isCompleted ? (
                            <Tag color="warning">{t("quizStatistics.player.awaitingOpponent")}</Tag>
                          ) : (
                            <Tag color={v ? "success" : "error"}>
                              {v ? t("quizStatistics.player.win") : t("quizStatistics.player.loss")}
                            </Tag>
                          ),
                      },
                      {
                        title: t("quizStatistics.player.score"),
                        key: "score",
                        width: 120,
                        align: "center",
                        render: (_: unknown, record: PlayerMatchHistoryDto) =>
                          record.isCompleted ? (
                            <Typography.Text strong>
                              <span style={{ color: "#52c41a" }}>{record.playerScore}</span>
                              {" : "}
                              <span style={{ color: "#ff4d4f" }}>{record.opponentScore}</span>
                            </Typography.Text>
                          ) : (
                            <Typography.Text type="secondary">—</Typography.Text>
                          ),
                      },
                    ]}
                  />
                ),
              },

              // ── Questions ─────────────────────────────────────────────────
              {
                key: "questions",
                label: (
                  <Space size={6} align="center">
                    <Target size={16} />
                    <span>{t("quizStatistics.player.tabQuestions")}</span>
                  </Space>
                ),
                children: playerStats.questions.length === 0 ? (
                  <Empty description={t("quizStatistics.noData")} />
                ) : (
                  <Table<PlayerQuestionStatDto>
                    rowKey="questionCopyId"
                    dataSource={playerStats.questions}
                    size="small"
                    pagination={{ pageSize: 15, hideOnSinglePage: true }}
                    scroll={{ x: 900 }}
                    columns={[
                      {
                        title: t("quizStatistics.player.question"),
                        dataIndex: "questionText",
                        key: "questionText",
                        ellipsis: true,
                        render: (v: string) => <Typography.Text>{stripHtml(v)}</Typography.Text>,
                      },
                      {
                        title: t("quizStatistics.player.category"),
                        dataIndex: "categoryName",
                        key: "categoryName",
                        width: 180,
                        filters: Array.from(
                          new Set(playerStats.questions.map((q) => q.categoryName).filter(Boolean))
                        ).map((cat) => ({ text: cat, value: cat })),
                        onFilter: (value, record: PlayerQuestionStatDto) => record.categoryName === value,
                        render: (v: string) => v ? <Tag color="blue" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{v}</Tag> : "—",
                      },
                      {
                        title: t("quizStatistics.player.playerAnswer"),
                        dataIndex: "playerAnswerText",
                        key: "playerAnswerText",
                        width: 220,
                        ellipsis: true,
                        render: (v: string, record: PlayerQuestionStatDto) => (
                          <Typography.Text
                            style={{ color: record.isCorrect ? "#52c41a" : "#ff4d4f" }}
                          >
                            {v}
                          </Typography.Text>
                        ),
                      },
                      {
                        title: t("quizStatistics.player.correct"),
                        dataIndex: "isCorrect",
                        key: "isCorrect",
                        width: 80,
                        align: "center",
                        filters: [
                          { text: t("quizStatistics.player.correct"), value: true },
                          { text: t("quizStatistics.player.incorrect"), value: false },
                        ],
                        onFilter: (value, record: PlayerQuestionStatDto) => record.isCorrect === value,
                        render: (v: boolean) => (
                          <Tag color={v ? "success" : "error"}>
                            {v ? "✓" : "✗"}
                          </Tag>
                        ),
                      },
                      {
                        title: t("quizStatistics.player.responseTime"),
                        dataIndex: "millisecondsTaken",
                        key: "millisecondsTaken",
                        width: 110,
                        align: "center",
                        sorter: (a: PlayerQuestionStatDto, b: PlayerQuestionStatDto) => a.millisecondsTaken - b.millisecondsTaken,
                        render: (v: number) => `${(v / 1000).toFixed(1)}s`,
                      },
                      {
                        title: t("quizStatistics.player.avgResponseTimeAll"),
                        dataIndex: "avgMillisecondsTakenAll",
                        key: "avgMillisecondsTakenAll",
                        width: 110,
                        align: "center",
                        render: (v: number, record: PlayerQuestionStatDto) => {
                          const playerMs = record.millisecondsTaken;
                          const diff = playerMs - v;
                          const color = diff > 2000 ? "#ff4d4f" : diff < -1000 ? "#52c41a" : "#888";
                          return (
                            <Space direction="vertical" size={0}>
                              <Typography.Text style={{ fontSize: 12 }}>{`${(v / 1000).toFixed(1)}s`}</Typography.Text>
                              <Typography.Text style={{ fontSize: 11, color }}>
                                {diff > 0 ? `+${(diff / 1000).toFixed(1)}s` : `${(diff / 1000).toFixed(1)}s`}
                              </Typography.Text>
                            </Space>
                          );
                        },
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        )}
      </Drawer>
    </motion.div>
  );
};
